// Copyright (c) Bentley Systems
import { DEFAULT_COLOR } from "./constants";

/**
 * Resolves a color value to a valid hex color, using DEFAULT_COLOR as fallback.
 *
 * @param color - The color value to resolve (may be null/undefined)
 * @param allowNull - Whether to allow null values (default: false)
 * @returns A valid hex color string or null if allowNull is true
 *
 * @example
 * ```typescript
 * resolveColorValue("#FF0000") // Returns "#FF0000"
 * resolveColorValue(null) // Returns DEFAULT_COLOR
 * resolveColorValue(null, true) // Returns null
 * resolveColorValue(undefined) // Returns DEFAULT_COLOR
 * ```
 */
export function resolveColorValue(
  color: string | null | undefined,
  allowNull: boolean = false,
): string | null {
  if (color !== null && color !== undefined && color !== "") {
    return color;
  }
  return allowNull ? null : DEFAULT_COLOR;
}

/**
 * Validates if a string is a valid hex color format.
 *
 * @param color - The color string to validate
 * @returns true if valid hex color format (#RRGGBB), false otherwise
 *
 * @example
 * ```typescript
 * isValidHexColor("#50CE67") // Returns true
 * isValidHexColor("#FFF") // Returns false (must be 6 digits)
 * isValidHexColor("50CE67") // Returns false (must start with #)
 * isValidHexColor(null) // Returns false
 * ```
 */
export function isValidHexColor(color: string | null | undefined): boolean {
  if (!color) {
    return false;
  }
  return /^#[0-9A-Fa-f]{6}$/.test(color);
}
